import React from 'react';
import { useTranslation } from 'react-i18next';
import { Budget, Transaction } from '../types/budget';
import { formatCurrency, getDisplayLabel } from '../utils/budget';
import { PencilIcon, TrashIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';

interface TransactionTableRowProps {
  transaction: Transaction;
  budget: Budget;
  index: number;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transaction: Transaction) => void;
  showActions?: boolean;
  onCreateInverse?: (transaction: Transaction) => void;
}

export const TransactionTableRow: React.FC<TransactionTableRowProps> = ({
  transaction,
  budget,
  index,
  onEdit,
  onDelete,
  showActions = true,
  onCreateInverse,
}) => {
  const { t } = useTranslation();

  const handleCreateInverse = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onCreateInverse) return;

    // Create a copy of the transaction with inverse amount
    const inverseTransaction: Transaction = {
      ...transaction,
      amount: -transaction.amount,
    };

    onCreateInverse(inverseTransaction);
  };

  const handleRowClick = () => {
    onEdit(transaction);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(transaction);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(transaction);
  };

  return (
    <tr
      className={`${index % 2 === 0 ? 'bg-white dark:bg-surface-800' : 'bg-slate-50 dark:bg-surface-700'} cursor-pointer hover:bg-slate-100 dark:hover:bg-surface-600`}
      onClick={handleRowClick}
    >
      <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-900 dark:text-slate-300 sm:px-6">
        {transaction.description}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-900 dark:text-slate-300 sm:px-6">
        {getDisplayLabel(transaction.responsible, budget.responsibles)}
      </td>
      <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-slate-900 dark:text-slate-300 sm:px-6 lg:table-cell">
        {getDisplayLabel(transaction.category, budget.categories)}
      </td>
      <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-slate-900 dark:text-slate-300 sm:px-6 lg:table-cell">
        {getDisplayLabel(transaction.destination, budget.destinations)}
      </td>
      <td
        className={`whitespace-nowrap px-3 py-4 text-right text-sm font-medium sm:px-6 ${
          transaction.amount >= 0
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-rose-600 dark:text-rose-400'
        }`}
      >
        {formatCurrency(transaction.amount, budget.settings.currency)}
      </td>
      {showActions && (
        <td
          className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-end space-x-2">
            <button
              onClick={handleEdit}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              title={t('common.edit')}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            {onCreateInverse && (
              <button
                onClick={handleCreateInverse}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                title={
                  transaction.amount < 0
                    ? t('transaction.createExpenseReport')
                    : t('transaction.createCashRefund')
                }
              >
                <ArrowsRightLeftIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={handleDelete}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              title={t('common.delete')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};
